import { useEffect, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import type { BlokKategorii, Moznost } from "@/components/cennik/CennikSidebar";
import { ANY } from "@/components/cennik/cennikZalozky";
import { formatPocet } from "@/lib/format";
import { bezDiakritiky } from "@/lib/hladanie";
import { formatKategoria } from "@/lib/kategorie";
import { cn } from "@/lib/utils";

/** Tlačidlo filtra v riadku filtrov (Cenník aj výbery z cenníka). */
export const FILTER_TLACIDLO =
  "h-9 w-auto max-w-[18rem] gap-1.5 rounded-full px-3 text-sm font-medium [&>span]:truncate";
/** Zapnutý filter svieti farbou appky. */
export const FILTER_ZAPNUTY = "border-primary bg-tandem-red-light text-tandem-red-dark";

/**
 * Kategória s hľadaním (KOLO 199 v Cenníku, od KOLA 201 aj vo všetkých
 * výberoch z cenníka). User: *„S hľadaním ako na ukážke"* — v Kovaní je
 * ~45 kategórií v skupinách, rozbaľovačka bez hľadania sa dala len
 * prechádzať. Vlastný panel (v `ui/` Popover nie je): zavrie ho klik mimo,
 * Esc alebo výber. Hľadá bez diakritiky; skupina, ktorej meno sedí,
 * ostane celá.
 */
export function KategoriaVyberu({
  bloky,
  hodnota,
  onVyber,
  ariaLabel = "Filter kategórie",
  onOtvorenie,
  zrusitelna = false,
}: {
  /** Možnosti s počtami (`kategorieMoznosti` z faset); `undefined` = načítava sa. */
  bloky: BlokKategorii[] | undefined;
  /** Vybraná kategória, `ANY` = všetky. */
  hodnota: string;
  onVyber: (hodnota: string) => void;
  ariaLabel?: string;
  /** Prvé otvorenie — výber z cenníka si fasety pýta až vtedy. */
  onOtvorenie?: () => void;
  /**
   * Vybraná kategória má na tlačidle ✕ (KOLO 202) — výber z cenníka nemá
   * riadok kapsúl ako Cenník, a kategória pri hľadaní ostáva, takže cesta
   * späť musí byť priamo na nej.
   */
  zrusitelna?: boolean;
}): JSX.Element {
  const [otvorene, setOtvorene] = useState(false);
  const [text, setText] = useState("");
  const obal = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!otvorene) return;
    function mimo(e: MouseEvent): void {
      if (obal.current && !obal.current.contains(e.target as Node)) setOtvorene(false);
    }
    function esc(e: KeyboardEvent): void {
      // Esc zavrie len panel, nie okno, v ktorom panel je — Radix Dialog
      // počúva na `document` v capture fáze, `window` je pred ním
      if (e.key === "Escape") {
        e.stopPropagation();
        setOtvorene(false);
      }
    }
    document.addEventListener("mousedown", mimo);
    window.addEventListener("keydown", esc, true);
    return () => {
      document.removeEventListener("mousedown", mimo);
      window.removeEventListener("keydown", esc, true);
    };
  }, [otvorene]);

  const vsetky = bloky ?? [];
  const hodnoty = new Set(
    vsetky.flatMap((b) =>
      b.typ === "moznost" ? [b.moznost.hodnota] : b.moznosti.map((m) => m.hodnota),
    ),
  );
  const hladane = bezDiakritiky(text.trim());
  const sedi = (label: string): boolean => !hladane || bezDiakritiky(label).includes(hladane);
  const viditelne = vsetky
    .map((b) => {
      if (b.typ === "moznost") return sedi(b.moznost.label) ? b : null;
      if (sedi(b.nadpis)) return b;
      const moznosti = b.moznosti.filter((m) => sedi(m.label));
      return moznosti.length ? { ...b, moznosti } : null;
    })
    .filter((b): b is NonNullable<typeof b> => b !== null);

  const zapnute = hodnota !== ANY;
  const sKrizikom = zrusitelna && zapnute;
  function vyber(h: string): void {
    onVyber(h);
    setOtvorene(false);
    setText("");
  }

  return (
    <div ref={obal} className="relative">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={otvorene}
        aria-haspopup="listbox"
        onClick={() => {
          if (!otvorene) onOtvorenie?.();
          setOtvorene((o) => !o);
        }}
        className={cn(
          "inline-flex items-center rounded-full border border-input bg-background ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          FILTER_TLACIDLO,
          zapnute && FILTER_ZAPNUTY,
          // miesto pre ✕ — tlačidlo v tlačidle HTML nedovolí, ✕ je vedľa
          sKrizikom && "pr-8",
        )}
      >
        <span>{zapnute ? `Kategória: ${formatKategoria(hodnota)}` : "Kategória"}</span>
        {sKrizikom ? null : <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />}
      </button>
      {sKrizikom ? (
        <button
          type="button"
          aria-label="Zrušiť kategóriu"
          title="Zrušiť kategóriu"
          onClick={() => vyber(ANY)}
          className="absolute right-1.5 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-tandem-red-dark hover:bg-background"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
      {otvorene ? (
        <div className="absolute left-0 z-30 mt-1 w-80 max-w-[calc(100vw-2rem)] rounded-md border bg-popover p-2 text-popover-foreground shadow-md">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Hľadať kategóriu"
              aria-label="Hľadať kategóriu"
              className="h-8 pl-8 text-sm"
            />
          </label>
          <div role="listbox" aria-label="Kategórie" className="mt-2 max-h-80 overflow-y-auto">
            <MoznostKategorie
              moznost={{ hodnota: ANY, label: "Všetky kategórie", pocet: -1 }}
              vybrana={!zapnute}
              onVyber={vyber}
            />
            {/* Vybraná kategória, ktorú fasety (ešte) nevrátili */}
            {zapnute && !hodnoty.has(hodnota) ? (
              <MoznostKategorie
                moznost={{ hodnota, label: formatKategoria(hodnota), pocet: 0 }}
                vybrana
                onVyber={vyber}
              />
            ) : null}
            {viditelne.map((b) =>
              b.typ === "moznost" ? (
                <MoznostKategorie
                  key={b.moznost.hodnota}
                  moznost={b.moznost}
                  vybrana={hodnota === b.moznost.hodnota}
                  onVyber={vyber}
                />
              ) : (
                <div key={b.kluc} role="group" aria-label={b.nadpis}>
                  <p className="px-2 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {b.nadpis}
                  </p>
                  {b.moznosti.map((m) => (
                    <MoznostKategorie
                      key={m.hodnota}
                      moznost={m}
                      // „— všetko" (hodnota rodiča) nie je odsadená
                      odsadena={b.kluc === "__ostatne__" || m.hodnota !== b.kluc}
                      vybrana={hodnota === m.hodnota}
                      onVyber={vyber}
                    />
                  ))}
                </div>
              ),
            )}
            {bloky === undefined ? (
              <p className="px-2 py-3 text-sm text-muted-foreground">Načítavam…</p>
            ) : viditelne.length === 0 ? (
              <p className="px-2 py-3 text-sm text-muted-foreground">
                {text.trim() ? `Žiadna kategória „${text.trim()}".` : "Žiadne kategórie."}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MoznostKategorie({
  moznost,
  vybrana,
  odsadena = false,
  onVyber,
}: {
  moznost: Moznost;
  vybrana: boolean;
  odsadena?: boolean;
  onVyber: (hodnota: string) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="option"
      aria-selected={vybrana}
      onClick={() => onVyber(moznost.hodnota)}
      className={cn(
        "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent",
        odsadena && "pl-5",
        vybrana && "bg-accent font-semibold",
        moznost.pocet === 0 && "text-muted-foreground",
      )}
    >
      <span className="min-w-0 flex-1 truncate">{moznost.label}</span>
      {moznost.pocet >= 0 ? (
        <span className="shrink-0 font-mono text-xs text-muted-foreground">
          ({formatPocet(moznost.pocet)})
        </span>
      ) : null}
    </button>
  );
}
